// src/components/store/ProductDetailView.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Heart,
  Share2,
  ShoppingBag,
  ShieldCheck,
  RefreshCw,
  Truck,
  Rotate3d,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Check,
  Zap,
  Package,
  Layers,
  Star,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import ProductReviewSection from "./ProductReviewSection";
import { saveAffiliateAttributionLocally } from "./AffiliateTracker";

// লোকালহোস্ট এবং লাইভ ডোমেইনে সঠিক ইমেজ পাথ নির্ধারণ
function resolveProductImageUrl(item: any): string {
  if (!item) return "";

  let rawPath = "";
  if (typeof item === "string") {
    rawPath = item;
  } else if (typeof item === "object") {
    rawPath = item.image || item.image_url || item.url || item.file || item.media_file || "";
  }

  if (!rawPath || typeof rawPath !== "string") return "";

  if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) {
    if (
      typeof window !== "undefined" &&
      !window.location.hostname.includes("localhost") &&
      !window.location.hostname.includes("127.0.0.1")
    ) {
      try {
        const parsed = new URL(rawPath);
        if (parsed.hostname.includes("127.0.0.1") || parsed.hostname.includes("localhost")) {
          return parsed.pathname;
        }
      } catch {
        return rawPath;
      }
    }
    return rawPath;
  }

  let cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  if (!cleanPath.startsWith("/media/") && !cleanPath.startsWith("/static/")) {
    cleanPath = `/media${cleanPath}`;
  }

  return cleanPath;
}

// AI ডেসক্রিপশন ফরম্যাটিং কম্পোনেন্ট
function FormattedDescription({ content }: { content: string }) {
  if (!content) {
    return <p className="text-slate-400 text-xs">পণ্যটির কোনো বিস্তারিত বিবরণ যোগ করা হয়নি।</p>;
  }

  const lines = content.split("\n").map((l) => l.trim()).filter(Boolean);

  const introParagraphs: string[] = [];
  const bulletPoints: string[] = [];
  let isFeatureSection = false;

  lines.forEach((line) => {
    const isBullet =
      line.startsWith("•") ||
      line.startsWith("-") ||
      line.startsWith("*") ||
      line.startsWith("✓");

    const isHeader =
      line.includes("বৈশিষ্ট্য") ||
      line.includes("Features") ||
      line.includes("Highlights");

    if (isHeader) {
      isFeatureSection = true;
      return;
    }

    if (isBullet || isFeatureSection) {
      const cleanLine = line.replace(/^[•\-\*✓\s]+/, "").trim();
      if (cleanLine) bulletPoints.push(cleanLine);
    } else {
      introParagraphs.push(line);
    }
  });

  return (
    <div className="space-y-6 font-sans text-slate-700">
      {introParagraphs.length > 0 && (
        <div className="space-y-3">
          {introParagraphs.map((para, idx) => (
            <p
              key={idx}
              className={`text-xs sm:text-sm leading-relaxed ${
                idx === 0
                  ? "font-bold text-slate-900 text-sm sm:text-base border-l-4 border-emerald-600 pl-3 py-0.5"
                  : "text-slate-600"
              }`}
            >
              {para}
            </p>
          ))}
        </div>
      )}

      {bulletPoints.length > 0 && (
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3">
          <h4 className="text-xs sm:text-sm font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span>মূল বৈশিষ্ট্য ও সুবিধাসমূহ:</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            {bulletPoints.map((bullet, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2.5 p-2.5 rounded-xl bg-white border border-slate-200/70 text-xs sm:text-sm text-slate-800 shadow-2xs"
              >
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 stroke-[3]" />
                </div>
                <span className="leading-snug">{bullet}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductDetailView({
  product,
  subdomain,
}: {
  product: any;
  subdomain?: string;
}) {
  const router = useRouter();
  const { addToCart, openCart } = useCart();
  const activeSubdomain = (subdomain || "nextlook").trim();

  // 🌟 প্রোডাক্ট পেজে (?ref=AFF...) ভিজিট করা মাত্রই সরাসরি রেফারেল ক্লিক ট্র্যাক ও লোকাল/কুকিতে সংরক্ষণ
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const refParam =
      params.get("ref") ||
      params.get("referral_code") ||
      params.get("affiliate");
    if (!refParam) return;

    const linkCode = params.get("lnk") || "";
    const sigToken = params.get("sig") || "";
    const campId = params.get("camp") || "";
    const utmSource = params.get("utm_source") || "direct";

    // সাথে সাথে localStorage ও Cookie-তে সংরক্ষণ (যাতে Buy Now বাটনে ক্লিক করলেও রেফারেল মিস না হয়)
    saveAffiliateAttributionLocally(activeSubdomain, {
      referral_code: refParam.trim().split(".")[0],
      signed_token: sigToken,
      campaign_id: campId,
    });

    const prodSlug = product?.slug || "";
    const trackKey = `xylo_aff_tracked_${activeSubdomain}_${refParam}_${linkCode}_${prodSlug || product?.id || ""}`;
    if (sessionStorage.getItem(trackKey)) return;
    sessionStorage.setItem(trackKey, "1");

    const host = window.location.hostname;
    const isLocal = host.includes("localhost") || host === "127.0.0.1";
    const apiBase = isLocal ? `http://${activeSubdomain}.localhost:8000` : "";

    fetch(`${apiBase}/api/v1/affiliates/track/?subdomain=${encodeURIComponent(activeSubdomain)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Tenant-Subdomain": activeSubdomain,
      },
      credentials: "include",
      body: JSON.stringify({
        ref: refParam.trim(),
        lnk: linkCode,
        sig: sigToken,
        camp: campId,
        utm_source: utmSource,
        product_id: product?.id || null,
        product_slug: prodSlug,
        path: window.location.pathname || "/",
      }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.tracked && data?.referral_code) {
          saveAffiliateAttributionLocally(activeSubdomain, {
            referral_code: data.referral_code,
            click_token: data.click_token || "",
            signed_token: data.signed_token || sigToken || "",
            campaign_id: campId || "",
            attribution_model: data.attribution_model || "last_click",
            cookie_duration_days: Number(data.cookie_duration_days) || 30,
            expires_at: data.expires_at,
          });
        }
      })
      .catch(() => {});
  }, [activeSubdomain, product?.id, product?.slug]);

  // ব্যাকএন্ড ইমেজগুলো সংগ্রহ
  const rawList: any[] = [];
  if (product?.primary_image) rawList.push(product.primary_image);
  if (product?.image) rawList.push(product.image);

  const inlineImages = product?.images || product?.product_images || product?.productimage_set || [];
  if (Array.isArray(inlineImages)) {
    rawList.push(...inlineImages);
  }

  const formattedImages = rawList
    .map((img) => resolveProductImageUrl(img))
    .filter((url) => url.length > 0);

  const images = Array.from(new Set(formattedImages));

  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string>(images[0] || "");
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [autoRemoveBg, setAutoRemoveBg] = useState(false);
  const [processedImg, setProcessedImg] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<"desc" | "spec" | "reviews">("desc");

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  // নতুন প্রোডাক্টে নেভিগেট করলে গ্যালারি ইনডেক্স রিসেট করা
  useEffect(() => {
    setCurrentIdx(0);
    setImageLoadError(false);
    setAutoRemoveBg(false);
    setProcessedImg(null);
  }, [product?.id, product?.slug]);

  useEffect(() => {
    if (images.length > 0 && images[currentIdx]) {
      setSelectedImage(images[currentIdx]);
      setImageLoadError(false);
    } else {
      setSelectedImage(images[0] || "");
    }
  }, [currentIdx, images]);

  // ব্যাকগ্রাউন্ড রিমুভার হ্যান্ডলিং
  useEffect(() => {
    if (!autoRemoveBg || !selectedImage || imageLoadError) {
      setProcessedImg(null);
      return;
    }

    let isMounted = true;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = selectedImage;

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        const bgR = data[0];
        const bgG = data[1];
        const bgB = data[2];
        const threshold = 40;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          const dist = Math.sqrt(
            Math.pow(r - bgR, 2) + Math.pow(g - bgG, 2) + Math.pow(b - bgB, 2)
          );

          const isWhiteBg = r > 235 && g > 235 && b > 235;

          if (dist < threshold || isWhiteBg) {
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(imgData, 0, 0);
        if (isMounted) {
          setProcessedImg(canvas.toDataURL("image/png"));
        }
      } catch {
        if (isMounted) setProcessedImg(null);
      }
    };

    img.onerror = () => {
      if (isMounted) setProcessedImg(null);
    };

    return () => {
      isMounted = false;
    };
  }, [selectedImage, autoRemoveBg, imageLoadError]);

  const scrollThumbnails = (dir: "left" | "right") => {
    if (thumbnailContainerRef.current) {
      const scrollAmount = dir === "left" ? -160 : 160;
      thumbnailContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const prevImage = () => {
    setCurrentIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (addToCart) addToCart(product, quantity);
    if (openCart) openCart();
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (addToCart) addToCart(product, quantity);
    router.push("/checkout");
  };

  const currentPrice = Number(product?.discount_price || product?.sale_price || product?.price || 0);
  const regularPrice = Number(product?.compare_price || product?.regular_price || 0);
  const hasDiscount = regularPrice > currentPrice && currentPrice > 0;
  const discountPercent =
    product?.discount_percentage ||
    (hasDiscount ? Math.round(((regularPrice - currentPrice) / regularPrice) * 100) : 0);

  const isInStock = product?.stock !== undefined ? product.stock > 0 : product?.is_in_stock !== false;
  const activeDisplaySrc = autoRemoveBg && processedImg ? processedImg : selectedImage;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 space-y-8 sm:space-y-12">
      {/* ১. ব্রেডক্রাম্ব */}
      <nav className="text-xs text-slate-400 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap py-1">
        <Link href="/" className="hover:text-emerald-600 transition-colors font-medium">Home</Link>
        <span>/</span>
        {product?.category_name && (
          <>
            <span className="text-slate-500 font-medium">{product.category_name}</span>
            <span>/</span>
          </>
        )}
        <span className="font-bold text-slate-800 truncate">{product?.name || "Product"}</span>
      </nav>

      {/* ২. মেইন প্রোডাক্ট ডিসপ্লে গ্রিড */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        
        {/* বাম পাশ: ইমেজ শোকেস (৬ কলাম) */}
        <div className="lg:col-span-6 space-y-4">
          <div className="relative aspect-square w-full rounded-3xl bg-slate-50/80 border border-slate-200/80 overflow-hidden shadow-xs flex items-center justify-center p-4">
            
            {/* কন্ট্রোল বাটন */}
            <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsRotating(!isRotating)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs cursor-pointer ${
                  isRotating
                    ? "bg-emerald-600 text-white ring-2 ring-emerald-400"
                    : "bg-white/95 hover:bg-white text-slate-700 border border-slate-200"
                }`}
              >
                <Rotate3d className={`w-3.5 h-3.5 ${isRotating ? "animate-spin" : ""}`} />
                <span>3D View</span>
              </button>

              <button
                type="button"
                onClick={() => setAutoRemoveBg(!autoRemoveBg)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs cursor-pointer ${
                  autoRemoveBg
                    ? "bg-amber-500 text-white ring-2 ring-amber-400"
                    : "bg-white/95 hover:bg-white text-slate-700 border border-slate-200"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Clean BG</span>
              </button>
            </div>

            {/* প্রোডাক্ট ছবি */}
            {activeDisplaySrc && !imageLoadError ? (
              <div
                className={`relative w-full h-full flex items-center justify-center select-none ${
                  isRotating ? "animate-luxury-3d-turn" : ""
                }`}
              >
                <Image
                  src={activeDisplaySrc}
                  alt={product?.name || "Product Showcase"}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  unoptimized
                  priority
                  onError={() => setImageLoadError(true)}
                  className="object-contain p-4 transition-all duration-300 drop-shadow-md"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-300 gap-2">
                <ShoppingBag className="w-16 h-16 stroke-1" />
                <span className="text-xs font-semibold">No Image Available</span>
              </div>
            )}

            {/* গ্যালারি নেভিগেশন অ্যারো */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-md flex items-center justify-center cursor-pointer transition-all active:scale-95"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-md flex items-center justify-center cursor-pointer transition-all active:scale-95"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* থাম্বনেইল তালিকা */}
          {images.length > 1 && (
            <div className="relative flex items-center pt-1">
              {images.length > 4 && (
                <button
                  type="button"
                  onClick={() => scrollThumbnails("left")}
                  className="absolute -left-2 z-10 w-7 h-7 rounded-full bg-white text-slate-700 shadow-md flex items-center justify-center border border-slate-200 cursor-pointer"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
              )}

              <div
                ref={thumbnailContainerRef}
                className="flex gap-3 overflow-x-auto scrollbar-none py-1 scroll-smooth w-full px-0.5"
              >
                {images.map((imgUrl: string, idx: number) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentIdx(idx)}
                    className={`relative w-18 h-18 rounded-2xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer bg-white p-1.5 flex items-center justify-center ${
                      currentIdx === idx
                        ? "border-emerald-600 ring-2 ring-emerald-500/20 shadow-sm scale-102"
                        : "border-slate-200 hover:border-slate-300 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <Image
                      src={imgUrl}
                      alt={`Thumbnail ${idx + 1}`}
                      fill
                      sizes="72px"
                      unoptimized
                      className="object-contain p-1"
                    />
                  </button>
                ))}
              </div>

              {images.length > 4 && (
                <button
                  type="button"
                  onClick={() => scrollThumbnails("right")}
                  className="absolute -right-2 z-10 w-7 h-7 rounded-full bg-white text-slate-700 shadow-md flex items-center justify-center border border-slate-200 cursor-pointer"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ডান পাশ: প্রোডাক্ট বিবরণী ও অ্যাকশন (৬ কলাম) */}
        <div className="lg:col-span-6 space-y-6">
          <div className="space-y-3 border-b border-slate-100 pb-5">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full ${
                  isInStock
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-rose-50 text-rose-600 border border-rose-200"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isInStock ? "bg-emerald-500" : "bg-rose-500"}`} />
                {isInStock ? "In Stock" : "Stock Out"}
              </span>

              {product?.category_name && (
                <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                  {product.category_name}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
              {product?.name || "Product Name"}
            </h1>

            {product?.sku && (
              <p className="text-xs text-slate-400 font-mono">
                SKU: <span className="font-bold text-slate-600">{product.sku}</span>
              </p>
            )}

            {/* প্রাইজ সেকশন */}
            <div className="flex items-baseline gap-3 pt-2">
              <span className="text-3xl sm:text-4xl font-black text-slate-900 font-mono">
                ৳{currentPrice.toLocaleString()}
              </span>
              {hasDiscount && (
                <>
                  <span className="text-base sm:text-lg text-slate-400 line-through font-mono">
                    ৳{regularPrice.toLocaleString()}
                  </span>
                  <span className="text-xs font-black text-emerald-700 bg-emerald-100/70 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                    {discountPercent}% OFF
                  </span>
                </>
              )}
            </div>
          </div>

          {/* শর্ট ডেসক্রিপশন */}
          {product?.description && (
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-3">
              {product.description.split("\n")[0].replace(/[•\-\*]/g, "")}
            </p>
          )}

          {/* কোয়ান্টিটি ও বাটন */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-slate-700">পরিমাণ:</span>
              <div className="flex items-center border border-slate-200 rounded-2xl p-1 bg-white shadow-2xs">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 cursor-pointer active:scale-90 transition-all"
                >
                  -
                </button>
                <span className="w-10 text-center text-sm font-black text-slate-900 font-mono select-none">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 cursor-pointer active:scale-90 transition-all"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!isInStock}
                className={`w-full sm:flex-1 py-3.5 px-6 rounded-2xl font-bold text-xs sm:text-sm shadow-md flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 ${
                  isAdded
                    ? "bg-emerald-600 text-white"
                    : !isInStock
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/20"
                }`}
              >
                {isAdded ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
                <span>{isAdded ? "Added to Cart" : "Add to Cart"}</span>
              </button>

              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!isInStock}
                className="w-full sm:flex-1 py-3.5 px-6 rounded-2xl font-bold text-xs sm:text-sm bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-500/25 flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95"
              >
                <Zap className="w-4 h-4 fill-white" />
                <span>Buy Now (অর্ডার করুন)</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-11 h-11 rounded-2xl border border-slate-200 flex items-center justify-center text-slate-600 hover:text-rose-500 hover:border-rose-200 transition-colors cursor-pointer bg-white"
                  title="Add to Wishlist"
                >
                  <Heart className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="w-11 h-11 rounded-2xl border border-slate-200 flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-200 transition-colors cursor-pointer bg-white"
                  title="Share"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* ট্রাস্ট ব্যাজ */}
          <div className="grid grid-cols-3 gap-3 p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80">
            <div className="flex flex-col items-center text-center gap-1.5">
              <Truck className="w-5 h-5 text-blue-600" />
              <div className="text-[11px]">
                <strong className="block text-slate-800 font-bold">Fast Delivery</strong>
                <span className="text-slate-500 text-[10px]">সারা দেশে হোম ডেলিভারি</span>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5 border-x border-slate-200">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <div className="text-[11px]">
                <strong className="block text-slate-800 font-bold">100% Authentic</strong>
                <span className="text-slate-500 text-[10px]">আসল পণ্যের নিশ্চয়তা</span>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <RefreshCw className="w-5 h-5 text-indigo-600" />
              <div className="text-[11px]">
                <strong className="block text-slate-800 font-bold">Cash on Delivery</strong>
                <span className="text-slate-500 text-[10px]">দেখে নেওয়ার সুযোগ</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ৩. ট্যাব সেকশন */}
      <div className="pt-8 border-t border-slate-200">
        <div className="flex gap-4 sm:gap-8 border-b border-slate-200 pb-2 overflow-x-auto whitespace-nowrap">
          <button
            type="button"
            onClick={() => setActiveTab("desc")}
            className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === "desc"
                ? "border-b-2 border-emerald-600 text-emerald-700"
                : "text-slate-400 hover:text-slate-700"
            }`}
          >
            <Package className="w-4 h-4" />
            <span>Product Description</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("spec")}
            className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === "spec"
                ? "border-b-2 border-emerald-600 text-emerald-700"
                : "text-slate-400 hover:text-slate-700"
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Specifications</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("reviews")}
            className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === "reviews"
                ? "border-b-2 border-emerald-600 text-emerald-700"
                : "text-slate-400 hover:text-slate-700"
            }`}
          >
            <Star className="w-4 h-4" />
            <span>Reviews & Ratings</span>
          </button>
        </div>

        <div className="py-6 sm:py-8">
          {activeTab === "desc" && (
            <div className="max-w-4xl">
              <FormattedDescription content={product?.description || ""} />
            </div>
          )}

          {activeTab === "spec" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl text-xs sm:text-sm">
              {product?.name && (
                <div className="p-3.5 bg-slate-50 rounded-2xl flex justify-between border border-slate-100">
                  <span className="text-slate-500 font-medium">Product Title</span>
                  <span className="font-bold text-slate-800">{product.name}</span>
                </div>
              )}
              {product?.category_name && (
                <div className="p-3.5 bg-slate-50 rounded-2xl flex justify-between border border-slate-100">
                  <span className="text-slate-500 font-medium">Category</span>
                  <span className="font-bold text-slate-800">{product.category_name}</span>
                </div>
              )}
              {product?.unit && (
                <div className="p-3.5 bg-slate-50 rounded-2xl flex justify-between border border-slate-100">
                  <span className="text-slate-500 font-medium">Packaging Unit</span>
                  <span className="font-bold text-slate-800">{product.unit}</span>
                </div>
              )}
              {product?.stock !== undefined && (
                <div className="p-3.5 bg-slate-50 rounded-2xl flex justify-between border border-slate-100">
                  <span className="text-slate-500 font-medium">Current Stock</span>
                  <span className="font-bold text-slate-800">{product.stock} items</span>
                </div>
              )}
              {product?.sku && (
                <div className="p-3.5 bg-slate-50 rounded-2xl flex justify-between border border-slate-100">
                  <span className="text-slate-500 font-medium">Product SKU</span>
                  <span className="font-mono font-bold text-slate-800">{product.sku}</span>
                </div>
              )}
            </div>
          )}

          {activeTab === "reviews" && product?.id && (
            <ProductReviewSection productId={product.id} subdomain={subdomain || ""} />
          )}
        </div>
      </div>
    </div>
  );
}